import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import { ArrowRightSmall, Xmark } from '@peacecover/icon';
import { useGetSceneOriginalEpisodes } from '../../domains/scene/ui/hooks/useGetSceneOriginalEpisodes';
import { useGetSceneDetail } from '../../domains/scene/ui/hooks/useGetSceneDetail';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';

const SceneOriginalEpisodesPage = () => {
  const navigate = useNavigate();
  const handleHistoryBack = useHandleHistoryBack();
  const { sceneId } = useParams();

  const [currentEpisodeIndex, setCurrentEpisodeIndex] = useState(0);

  const {
    data: sceneOriginalEpisodes,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useGetSceneOriginalEpisodes(sceneId ?? '');
  const episodes = sceneOriginalEpisodes?.pages.flatMap((page) => page.sceneEpisodes ?? []) ?? [];

  const totalEpisodes = episodes.length;
  const currentEpisodeNumber =
    totalEpisodes > 0 ? Math.min(currentEpisodeIndex + 1, totalEpisodes) : 0;
  const progressRatio =
    totalEpisodes > 0
      ? currentEpisodeNumber / (hasNextPage ? totalEpisodes + 1 : totalEpisodes)
      : 0;
  const progressPercent = Math.round(progressRatio * 100);

  const { data: sceneDetail } = useGetSceneDetail(sceneId ?? '');

  const handleGoToPreview = () => {
    navigate(`/scene/${sceneId}/preview`);
  };

  return (
    <div className="min-h-screen pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] bg-background-light dark:bg-background-dark">
      <header className="flex items-center justify-between px-[16px] py-[10px] relative">
        <button type="button" onClick={handleHistoryBack}>
          <Xmark className="text-text-main-lv1" />
        </button>
        <p className="text-[17px] font-bold leading-[20px] text-center text-text-main-lv1 whitespace-nowrap absolute left-1/2 -translate-x-1/2">
          {sceneDetail?.title ?? ''}
        </p>
      </header>
      <div className="mt-[24px]">
        {isLoading || episodes.length === 0 ? null : (
          <Virtuoso
            useWindowScroll
            overscan={480}
            defaultItemHeight={40}
            style={{ minHeight: 100 }}
            data={episodes}
            atBottomThreshold={200}
            endReached={() => {
              if (!hasNextPage || isFetchingNextPage) return;
              fetchNextPage();
            }}
            rangeChanged={({ startIndex, endIndex }) => {
              if (episodes.length === 0) return;

              const clampedEndIndex = Math.min(endIndex, episodes.length - 1);
              // 화면에 보이는 구간의 가운데 인덱스를 현재 에피소드로 사용
              const middleIndex = Math.round((startIndex + clampedEndIndex) / 2);
              setCurrentEpisodeIndex(middleIndex);
            }}
            itemContent={(_index, episode) => (
              <div className="px-[17px]">
                <p
                  className="text-[19px] text-text-main-lv2 dark:text-[#FFF] leading-[40px] whitespace-pre-wrap"
                  style={{
                    fontFamily: '"Noto Serif KR", ui-serif, Georgia, "Times New Roman", serif'
                  }}
                >
                  {episode.content ?? ''}
                </p>
              </div>
            )}
          />
        )}
      </div>
      <div className="fixed bottom-0 left-0 right-0 flex flex-col gap-[10px] items-center pt-[16px] px-[16px] pb-[calc(env(safe-area-inset-bottom)+10px)] bg-pure-white dark:bg-pure-black shadow-[0_1px_5.8px_0_rgba(0,0,0,0.12)]">
        <div className="flex items-center gap-[10px] justify-between w-full">
          <div className="relative w-full">
            <div className="w-[307px] h-[6px] bg-black-opacity-200 dark:bg-white-opacity-200 rounded-[20px]" />
            <div
              className="h-[6px] rounded-[20px] bg-[linear-gradient(90deg,#41F7A2_0%,#5C3DFA_100%)] absolute top-0 left-0"
              style={{
                width: `${progressPercent}%`,
                maxWidth: 307
              }}
            />
          </div>
          <p className="text-[12px] leading-[14px] text-text-main-lv3 font-semibold">
            {progressPercent}%
          </p>
        </div>
        <div className="flex items-center justify-between w-full">
          <p className="text-[13px] font-medium leading-[16px] text-text-main-lv3">
            {totalEpisodes > 0
              ? `${currentEpisodeNumber} / ${totalEpisodes} 에피소드`
              : '에피소드 없음'}
          </p>
          <button
            type="button"
            className="bg-label rounded-full py-[4px] pl-[10px] pr-[4px] flex items-center gap-[2px] text-on-label text-[13px] leading-[16px] font-semibold"
            onClick={handleGoToPreview}
          >
            프리뷰 보러가기
            <ArrowRightSmall size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SceneOriginalEpisodesPage;
